import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { Container, Reveal, SectionHeading, useCollection } from "@/components/common";
import PageHero from "@/components/PageHero";
import EnquiryForm from "@/components/EnquiryForm";
import { getContent, getSiteContent } from "@/lib/api";
import { HandHeart, Users, Tent, Megaphone } from "@phosphor-icons/react";

const WAYS = [
  [HandHeart, "Donate", "Make a contribution to support cancer awareness, screening and patient care in underserved communities."],
  [Tent, "Sponsor a Camp", "Fund a village cancer awareness and screening camp and bring care where it is needed most."],
  [Megaphone, "Fundraise", "Start or join a fundraising campaign for NICCI and rally your network for the cause."],
  [Users, "Volunteer", "Give your time and skills to NICCI's grassroots programmes."],
];

export default function Support() {
  const [nicci, setNicci] = useState(null);
  const { data: campaigns } = useCollection(() => getContent("fundraising"), []);
  useEffect(() => { getSiteContent("nicci").then(setNicci).catch(() => {}); }, []);

  return (
    <div data-testid="support-page">
      <PageHero label="NICCI Foundation" title="Support Our Initiatives"
        intro="Your support helps bring cancer awareness, screening, education and care to villages and underserved communities across India."
        image="https://images.unsplash.com/photo-1654609160632-7324716196fb?crop=entropy&cs=srgb&fm=jpg" />

      {nicci && (
        <section className="bg-white py-16">
          <Container>
            <div className="grid gap-12 md:grid-cols-12">
              <div className="md:col-span-7">
                <SectionHeading label="Why It Matters" title="New India Cancer Charity Initiative" />
                <p className="text-base leading-relaxed text-softgrey-600">{nicci.mission}</p>
                {nicci.note && <p className="mt-4 text-sm italic text-softgrey-600">{nicci.note}</p>}
              </div>
              <div className="md:col-span-5">
                <div className="rounded-lg border border-slate-200 bg-softgrey-100 p-8">
                  <div className="overline text-gold-dark">Our Vision</div>
                  <p className="mt-3 font-serif text-2xl leading-snug text-navy">{nicci.vision}</p>
                </div>
              </div>
            </div>
          </Container>
        </section>
      )}

      <section className="bg-softgrey-100 py-16">
        <Container>
          <SectionHeading label="Ways to Support" title="Every contribution creates impact" />
          <div className="grid gap-px overflow-hidden rounded-lg border border-slate-200 bg-slate-200 sm:grid-cols-2 lg:grid-cols-4">
            {WAYS.map(([Icon, t, d], i) => (
              <Reveal key={t} delay={(i % 4) * 0.05}>
                <div className="h-full bg-white p-8">
                  <Icon size={28} weight="light" className="text-gold-dark" />
                  <h3 className="mt-4 font-serif text-2xl text-navy">{t}</h3>
                  <p className="mt-2 text-sm leading-relaxed text-softgrey-600">{d}</p>
                </div>
              </Reveal>
            ))}
          </div>
        </Container>
      </section>

      {campaigns.length > 0 && (
        <section className="bg-white py-16">
          <Container>
            <SectionHeading label="Campaigns" title="Current fundraising campaigns" />
            <div className="grid gap-6 md:grid-cols-2 lg:grid-cols-3">
              {campaigns.map((c, i) => (
                <Reveal key={c.id} delay={(i % 3) * 0.06}>
                  <div className="overflow-hidden rounded-lg border border-slate-200 bg-white">
                    {c.image && <div className="aspect-[16/9] overflow-hidden"><img src={c.image} alt={c.title} className="h-full w-full object-cover" /></div>}
                    <div className="p-6">
                      <h4 className="font-serif text-xl text-navy">{c.title}</h4>
                      {c.description && <p className="mt-2 text-sm text-softgrey-600">{c.description}</p>}
                      {(c.goal || c.raised) && <div className="mt-3 text-xs font-semibold text-gold-dark">{c.raised ? `${c.raised} raised` : ""}{c.goal ? ` · Goal ${c.goal}` : ""}</div>}
                    </div>
                  </div>
                </Reveal>
              ))}
            </div>
          </Container>
        </section>
      )}

      <section className="bg-navy py-16 text-white">
        <Container className="max-w-3xl">
          <SectionHeading light label="Get in Touch" title="Pledge your support" intro="Suggested contribution ₹500. Tell us how you would like to support NICCI and a member of the team will be in touch to arrange the details." />
          <div className="rounded-lg bg-white p-8">
            <EnquiryForm type="donation" submitLabel="Submit Support Pledge" fields={[
              { name: "name", label: "Full Name", required: true },
              { name: "email", label: "Email", type: "email", required: true },
              { name: "phone", label: "Phone" },
              { name: "country", label: "Country / Region" },
              { name: "subject", label: "How would you like to support?", options: ["Donate", "Sponsor a Camp", "Fundraise", "Volunteer", "Corporate / CSR Partnership", "Other"] },
              { name: "amount", label: "Suggested Contribution (INR)", options: ["₹500", "₹1,000", "₹2,500", "₹5,000", "Other / Custom"] },
              { name: "message", label: "Message / Details", area: true },
            ]} />
          </div>
        </Container>
      </section>
    </div>
  );
}
